import type { OrganizationIdInput } from '@/lib/organization-scoping'
import {
  createLocalPrintProfileRevisionGuard,
  createLocalPrintProfileWriteGuard,
  loadLocalPrintProfile,
  localPrintPendingStorageKey,
  saveLocalPrintPairing,
  type LocalPrintProfile,
} from '@/lib/local-print-profile'
import {
  createLocalPrintIntentCoordinator,
  type LocalPrintIntent,
} from '@/lib/local-print-intent-coordinator'
import type { SnapdragonTrayOptionSource } from '@/lib/services/snapdragon/snapdragon.settings'
import type { SnapdragonReceiptProtocol } from '@/lib/services/snapdragon/snapdragon.types'
import { readProperty } from '@/lib/utils'

export const localPrintProtocolVersion = 1
export const localPrintBaseUrl = 'http://127.0.0.1:17373/v1'
const localPrintRequestTimeoutMs = 10_000

/** `GET /health` from the Snapdragon desktop app. */
export interface LocalPrintHealth {
  protocolVersion: number | null
  computerId?: string
  machineId?: string
}

/** A printer the desktop app reports for this computer. */
export interface LocalPrintPrinter extends SnapdragonTrayOptionSource {
  id: string
  displayName?: string | null
  name?: string | null
  receiptProtocol?: string
}

/** `GET /station`: this computer's Snapdragon station and its printers. */
export interface LocalPrintStation {
  connected: boolean
  stationId?: string
  stationName?: string
  printers: LocalPrintPrinter[]
  /** Older desktop versions leave this out. */
  supportedReceiptProtocols?: string[]
}

/** `POST /pairings`. */
export interface LocalPrintPairingResult {
  token: string
  protocolVersion: number | null
}

/** A Direct Printing job as the desktop app reports it. */
export interface LocalPrintJob {
  id: string
  clientJobId?: string
  title?: string
  printerId?: string
  status?: string
  failureReason?: string
  message?: string
  /** Epoch seconds. */
  createdAt?: number
  /** Epoch seconds. */
  updatedAt?: number
}

/** `GET /jobs`. */
export interface LocalPrintJobs {
  jobs: LocalPrintJob[]
}

/** A file an HTML document embeds, in the shape `SalesOrderRenderSource` sends it. */
export interface LocalPrintDocumentAsset {
  name: string
  content_type: string
  content_base64: string
}

export type LocalPrintDocument =
  | {
      kind: 'html'
      title: string
      html: string
      css?: string
      assets?: LocalPrintDocumentAsset[]
    }
  | { kind: 'raster'; title: string; widthPx: number; heightPx: number; dataBase64: string }

export type LocalPrintOutput =
  | {
      mode: 'windows_pdf'
      printerId: string
      copies: number
      tray: string | null
      paperSize: string
      orientation: string
      cut: false
    }
  | {
      mode: SnapdragonReceiptProtocol
      printerId: string
      copies: number
      paperWidthMm: number
      cut: boolean
      openDrawer: boolean
      tray: null
    }

/** A print job as the desktop app's `POST /jobs` accepts it. */
export interface LocalPrintJobRequest {
  version: number
  organizationId: string
  clientJobId: string
  document: LocalPrintDocument
  output: LocalPrintOutput
}

export interface LocalPrintRequestOptions {
  signal?: AbortSignal
  timeoutMs?: number | null
}

interface LocalRequestOptions extends LocalPrintRequestOptions {
  method?: 'GET' | 'POST'
  token?: string
  body?: unknown
}

export interface LocalPrintErrorOptions {
  code?: string
  httpStatus?: number | null
  jobId?: string
  jobStatus?: string
  outcomeUnknown?: boolean
  cause?: unknown
}

export interface PairLocalPrintOptions extends LocalPrintRequestOptions {
  advanceRevision?: boolean
  pairingCode?: string
  refreshConnection?: boolean
  organizationId: OrganizationIdInput
  organizationName: string
  shouldPersist?: () => boolean
}

export interface ConnectLocalPrintStationOptions extends LocalPrintRequestOptions {
  organizationId: OrganizationIdInput
  organizationName: string
  health?: LocalPrintHealth
  refreshConnection?: boolean
  shouldPersist?: () => boolean
}

export interface LocalPrintStationConnection {
  health: LocalPrintHealth
  profile: LocalPrintProfile
  profileRevision: number | null
  station: LocalPrintStation
}

export interface LocalPrintIntentOptions {
  organizationId: OrganizationIdInput
  token: string
  intentKey: string
  clientJobId?: string
  buildRequest: (clientJobId: string) => Promise<LocalPrintJobRequest>
}

interface ActiveLocalPrintRequest<TResult> {
  promise: Promise<TResult>
  refreshConnection: boolean
  signal: AbortSignal | undefined
}

const activeLocalPrintPairings = new Map<string, ActiveLocalPrintRequest<LocalPrintPairingResult>>()
const activeLocalPrintConnections = new Map<
  string,
  ActiveLocalPrintRequest<LocalPrintStationConnection>
>()

export class LocalPrintError extends Error {
  code: string
  httpStatus: number | null
  jobId: string
  jobStatus: string
  outcomeUnknown: boolean

  constructor(
    message: string,
    {
      code = '',
      httpStatus = null,
      jobId = '',
      jobStatus = '',
      outcomeUnknown = false,
      cause,
    }: LocalPrintErrorOptions = {}
  ) {
    super(message)
    this.name = 'LocalPrintError'
    this.code = code
    this.httpStatus = httpStatus
    this.jobId = jobId
    this.jobStatus = jobStatus
    this.outcomeUnknown = outcomeUnknown
    if (cause) this.cause = cause
  }
}

const localPrintIntentCoordinator = createLocalPrintIntentCoordinator({
  pendingStorageKey: localPrintPendingStorageKey,
  createCoordinationError: (message, details) => new LocalPrintError(message, details),
})

// The desktop app's responses are JSON, so each field is checked.
function optionalString(value: unknown, key: string): string | undefined {
  const field = readProperty(value, key)
  return typeof field === 'string' ? field : undefined
}

/** Desktop ids can arrive as numbers; callers compare them as strings. */
function optionalId(value: unknown, key: string): string | undefined {
  const field = readProperty(value, key)
  return typeof field === 'string' || typeof field === 'number' ? String(field) : undefined
}

function optionalNumber(value: unknown, key: string): number | null {
  const field = readProperty(value, key)
  return typeof field === 'number' ? field : null
}

function optionalRecord(value: unknown, key: string): Record<string, unknown> | undefined {
  const field = readProperty(value, key)
  if (typeof field !== 'object' || field === null || Array.isArray(field)) return undefined
  return Object.fromEntries(Object.entries(field))
}

function readTrayOptions(value: unknown): LocalPrintPrinter['tray_options'] {
  if (!Array.isArray(value)) return undefined
  const options: unknown[] = value

  return options.map((option) => {
    const id = readProperty(option, 'id')
    return {
      id: typeof id === 'string' || typeof id === 'number' ? id : null,
      name: optionalString(option, 'name'),
      label: optionalString(option, 'label'),
    }
  })
}

function readLocalPrintPrinter(value: unknown): LocalPrintPrinter[] {
  const id = readProperty(value, 'id')
  if (typeof id !== 'string' && typeof id !== 'number') return []

  return [
    {
      id: String(id),
      displayName: optionalString(value, 'displayName'),
      name: optionalString(value, 'name'),
      receiptProtocol: optionalString(value, 'receiptProtocol'),
      tray_options: readTrayOptions(readProperty(value, 'tray_options')),
      supported_options: optionalRecord(value, 'supported_options'),
      supportedOptions: optionalRecord(value, 'supportedOptions'),
    },
  ]
}

function readLocalPrintHealth(payload: unknown): LocalPrintHealth {
  return {
    protocolVersion: optionalNumber(payload, 'protocolVersion'),
    computerId: optionalString(payload, 'computerId'),
    machineId: optionalString(payload, 'machineId'),
  }
}

function readLocalPrintStation(payload: unknown): LocalPrintStation {
  const printers = readProperty(payload, 'printers')
  const printerList: unknown[] = Array.isArray(printers) ? printers : []
  const protocols = readProperty(payload, 'supportedReceiptProtocols')
  const protocolList: unknown[] | undefined = Array.isArray(protocols) ? protocols : undefined

  return {
    connected: readProperty(payload, 'connected') === true,
    stationId: optionalString(payload, 'stationId'),
    stationName: optionalString(payload, 'stationName'),
    printers: printerList.flatMap(readLocalPrintPrinter),
    supportedReceiptProtocols: protocolList?.filter(
      (protocol): protocol is string => typeof protocol === 'string'
    ),
  }
}

function readLocalPrintPairing(payload: unknown): LocalPrintPairingResult {
  return {
    token: optionalString(payload, 'token') ?? '',
    protocolVersion: optionalNumber(payload, 'protocolVersion'),
  }
}

function readLocalPrintJob(payload: unknown): LocalPrintJob {
  return {
    id: optionalId(payload, 'id') ?? '',
    clientJobId: optionalString(payload, 'clientJobId'),
    title: optionalString(payload, 'title'),
    printerId: optionalId(payload, 'printerId'),
    status: optionalString(payload, 'status'),
    failureReason: optionalString(payload, 'failureReason'),
    message: optionalString(payload, 'message'),
    createdAt: optionalNumber(payload, 'createdAt') ?? undefined,
    updatedAt: optionalNumber(payload, 'updatedAt') ?? undefined,
  }
}

function readLocalPrintJobs(payload: unknown): LocalPrintJobs {
  const jobs = readProperty(payload, 'jobs')
  const jobList: unknown[] = Array.isArray(jobs) ? jobs : []

  return { jobs: jobList.map(readLocalPrintJob) }
}

export function localPrintNeedsPrinterCheck(error: unknown): boolean {
  const code = readProperty(error, 'code')
  return (
    readProperty(error, 'outcomeUnknown') === true ||
    (typeof code === 'string' &&
      [
        'job_acceptance_unknown',
        'job_status_unknown',
        'job_still_printing',
        'job_needs_attention',
      ].includes(code))
  )
}

function retainLocalPrintIntent(
  organizationId: OrganizationIdInput,
  intentKey: string,
  intent: LocalPrintIntent
) {
  return localPrintIntentCoordinator.retainIntent(organizationId, intentKey, intent)
}

function requireLocalPrintIntentRetention(
  organizationId: OrganizationIdInput,
  intentKey: string,
  intent: LocalPrintIntent
) {
  if (!retainLocalPrintIntent(organizationId, intentKey, intent)) {
    throw new LocalPrintError(
      'This browser could not safely remember the print job, so nothing was sent. Check browser storage and try again.',
      { code: 'job_intent_storage_failed' }
    )
  }
}

function clearLocalPrintIntent(organizationId: OrganizationIdInput, intentKey: string) {
  return localPrintIntentCoordinator.clearIntent(organizationId, intentKey)
}

function attachLocalPrintIntentRecovery<TError>(
  error: TError,
  organizationId: OrganizationIdInput,
  intentKey: string
): TError {
  if (typeof error === 'object' && error !== null) {
    Object.assign(error, { intentOrganizationId: String(organizationId), intentKey })
  }
  return error
}

export function canReleaseLocalPrintIntent(error: unknown): boolean {
  return Boolean(readProperty(error, 'intentOrganizationId') && readProperty(error, 'intentKey'))
}

export function releaseLocalPrintIntent(error: unknown): boolean {
  const intentOrganizationId = readProperty(error, 'intentOrganizationId')
  const intentKey = readProperty(error, 'intentKey')
  if (
    !canReleaseLocalPrintIntent(error) ||
    (typeof intentOrganizationId !== 'string' && typeof intentOrganizationId !== 'number') ||
    typeof intentKey !== 'string'
  ) {
    return false
  }
  return clearLocalPrintIntent(intentOrganizationId, intentKey)
}

async function localRequest(
  path: string,
  {
    method = 'GET',
    token = '',
    body,
    signal,
    timeoutMs = localPrintRequestTimeoutMs,
  }: LocalRequestOptions = {}
): Promise<unknown> {
  const controller = new AbortController()
  const abortFromCaller = () => controller.abort(signal?.reason)
  if (signal?.aborted) abortFromCaller()
  else signal?.addEventListener('abort', abortFromCaller, { once: true })
  const timeout =
    timeoutMs === null
      ? null
      : globalThis.setTimeout(
          () =>
            controller.abort(
              new DOMException('Direct Printing request timed out.', 'TimeoutError')
            ),
          timeoutMs
        )
  let response: Response
  let payload: unknown
  try {
    response = await fetch(`${localPrintBaseUrl}${path}`, {
      method,
      cache: 'no-store',
      signal: controller.signal,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    })
    try {
      payload = await response.json()
    } catch (error) {
      if (controller.signal.aborted) throw error
      payload = {}
    }
  } catch (error) {
    if (controller.signal.aborted && !signal?.aborted) {
      throw new LocalPrintError('Snapdragon did not respond in time.', {
        code: 'request_timeout',
        cause: error,
      })
    }
    throw error
  } finally {
    if (timeout !== null) globalThis.clearTimeout(timeout)
    signal?.removeEventListener('abort', abortFromCaller)
  }
  if (!response.ok) {
    const detail = readProperty(payload, 'error')
    throw new LocalPrintError(
      optionalString(detail, 'message') || 'Snapdragon Direct Printing is unavailable.',
      {
        code: optionalString(detail, 'code'),
        httpStatus: response.status,
        outcomeUnknown: readProperty(detail, 'outcomeUnknown') === true,
      }
    )
  }
  return payload
}

export async function getLocalPrintHealth(
  options?: LocalPrintRequestOptions
): Promise<LocalPrintHealth> {
  return readLocalPrintHealth(await localRequest('/health', options))
}

export async function getLocalPrintPermissionState(): Promise<PermissionState | 'unknown'> {
  const query = globalThis.navigator?.permissions?.query
  if (typeof query !== 'function') return 'unknown'

  for (const name of ['loopback-network', 'local-network-access']) {
    try {
      const permission = await query.call(globalThis.navigator.permissions, {
        // @ts-expect-error: TypeScript's `PermissionName` does not list Chrome's local network permissions yet.
        name,
      })
      return permission.state
    } catch {
      // Chrome renamed this permission when it split loopback from local-network access.
    }
  }

  return 'unknown'
}

export function pairLocalPrint({
  advanceRevision = true,
  pairingCode = '',
  refreshConnection = false,
  organizationId,
  organizationName,
  shouldPersist = () => true,
  signal,
  timeoutMs,
}: PairLocalPrintOptions): Promise<LocalPrintPairingResult> {
  const writeIsCurrent = createLocalPrintProfileWriteGuard(organizationId)
  const profileRevisionIsCurrent = createLocalPrintProfileRevisionGuard(organizationId)
  const request = async () =>
    readLocalPrintPairing(
      await localRequest('/pairings', {
        method: 'POST',
        body: {
          pairingCode,
          refreshConnection,
          product: 'tukios_flowers',
          organizationId: String(organizationId),
          organizationName,
        },
        signal,
        timeoutMs,
      })
    )

  // A setup page can mount more than one Direct Printing consumer at once.
  // Share automatic pairing so a second request cannot revoke the first request's fresh token.
  if (pairingCode) return request()

  const pairingKey = String(organizationId)
  const activePairing = activeLocalPrintPairings.get(pairingKey)
  if (activePairing && activePairing.signal === signal && !activePairing.signal?.aborted) {
    if (refreshConnection && !activePairing.refreshConnection) {
      return activePairing.promise.catch(() =>
        pairLocalPrint({
          advanceRevision,
          refreshConnection: true,
          organizationId,
          organizationName,
          shouldPersist,
          signal,
          timeoutMs,
        })
      )
    }
    return activePairing.promise
  }

  const pairOnce = async () => {
    try {
      const pair = async () => {
        const stored = loadLocalPrintProfile(organizationId)
        if (stored.token) {
          try {
            await getLocalPrintStation(stored.token, { signal, timeoutMs })
            return { token: stored.token, protocolVersion: localPrintProtocolVersion }
          } catch {
            // Another browser tab may have left an obsolete token. Replace it below.
          }
        }

        const result = await request()
        signal?.throwIfAborted()
        if (shouldPersist() && writeIsCurrent() && profileRevisionIsCurrent()) {
          saveLocalPrintPairing(
            organizationId,
            { ...stored, token: result.token },
            { advanceRevision }
          )
        }
        return result
      }
      const locks = globalThis.navigator?.locks
      if (typeof locks?.request === 'function') {
        return await locks.request(`snapdragon:local-print-pair:${pairingKey}`, pair)
      }
      return await pair()
    } finally {
      if (activeLocalPrintPairings.get(pairingKey)?.promise === pairing) {
        activeLocalPrintPairings.delete(pairingKey)
      }
    }
  }
  const pairing = pairOnce()
  activeLocalPrintPairings.set(pairingKey, { promise: pairing, refreshConnection, signal })
  return pairing
}

export async function getLocalPrintStation(
  token: string,
  options?: LocalPrintRequestOptions
): Promise<LocalPrintStation> {
  return readLocalPrintStation(await localRequest('/station', { ...options, token }))
}

export function connectLocalPrintStation({
  organizationId,
  organizationName,
  health: suppliedHealth,
  refreshConnection = false,
  shouldPersist = () => true,
  signal,
  timeoutMs,
}: ConnectLocalPrintStationOptions): Promise<LocalPrintStationConnection> {
  const writeIsCurrent = createLocalPrintProfileWriteGuard(organizationId)
  const profileRevisionIsCurrent = createLocalPrintProfileRevisionGuard(organizationId)
  const connectionKey = String(organizationId)
  const activeConnection = activeLocalPrintConnections.get(connectionKey)
  if (activeConnection && activeConnection.signal === signal && !activeConnection.signal?.aborted) {
    if (refreshConnection && !activeConnection.refreshConnection) {
      return activeConnection.promise.catch(() =>
        connectLocalPrintStation({
          organizationId,
          organizationName,
          health: suppliedHealth,
          refreshConnection: true,
          shouldPersist,
          signal,
          timeoutMs,
        })
      )
    }
    return activeConnection.promise
  }

  const connectOnce = async (): Promise<LocalPrintStationConnection> => {
    try {
      const health = suppliedHealth ?? (await getLocalPrintHealth({ signal, timeoutMs }))
      const stored = loadLocalPrintProfile(organizationId, health.computerId)
      let token = stored.token
      let station: LocalPrintStation | null = null

      if (token) {
        try {
          station = await getLocalPrintStation(token, { signal, timeoutMs })
        } catch {
          token = ''
        }
      }

      // A failed station read clears the token, so this is the same as checking the token.
      if (!station) {
        const pairing = await pairLocalPrint({
          advanceRevision: false,
          refreshConnection,
          organizationId,
          organizationName,
          shouldPersist,
          signal,
          timeoutMs,
        })
        token = pairing.token
        station = await getLocalPrintStation(token, { signal, timeoutMs })
      }

      const profile = {
        ...stored,
        token,
        computerId: health.computerId ?? '',
        machineId: health.machineId ?? '',
        stationId: station.stationId ?? '',
        stationName: station.stationName ?? '',
      }
      signal?.throwIfAborted()
      const profileRevision =
        shouldPersist() && writeIsCurrent() && profileRevisionIsCurrent()
          ? saveLocalPrintPairing(organizationId, profile)
          : null

      return { health, profile, profileRevision, station }
    } finally {
      if (activeLocalPrintConnections.get(connectionKey)?.promise === connection) {
        activeLocalPrintConnections.delete(connectionKey)
      }
    }
  }
  const connection = connectOnce()
  activeLocalPrintConnections.set(connectionKey, {
    promise: connection,
    refreshConnection,
    signal,
  })
  return connection
}

export async function getLocalPrintJob(
  token: string,
  jobId: string,
  options?: LocalPrintRequestOptions
): Promise<LocalPrintJob> {
  return readLocalPrintJob(
    await localRequest(`/jobs/${encodeURIComponent(jobId)}`, { ...options, token })
  )
}

export async function listLocalPrintJobs(
  token: string,
  options?: LocalPrintRequestOptions
): Promise<LocalPrintJobs> {
  return readLocalPrintJobs(await localRequest('/jobs', { ...options, token }))
}

export async function findLocalPrintJob(
  token: string,
  clientJobId: string
): Promise<LocalPrintJob> {
  return readLocalPrintJob(
    await localRequest('/jobs/lookup', {
      method: 'POST',
      token,
      body: { clientJobId },
    })
  )
}

export async function submitLocalPrintJob(
  token: string,
  request: LocalPrintJobRequest
): Promise<LocalPrintJob> {
  try {
    const job = readLocalPrintJob(
      await localRequest('/jobs', { method: 'POST', token, body: request })
    )
    if (!job.id) {
      throw new LocalPrintError(
        'Snapdragon could not confirm whether this print job was accepted. Check the printer before printing another copy.',
        { code: 'job_acceptance_unknown', outcomeUnknown: true }
      )
    }
    return job
  } catch (error) {
    if (error instanceof LocalPrintError && (error.httpStatus || error.outcomeUnknown)) throw error

    throw new LocalPrintError(
      'Snapdragon could not confirm whether this print job was accepted. Check the printer before printing another copy.',
      { code: 'job_acceptance_unknown', outcomeUnknown: true, cause: error }
    )
  }
}

export async function waitForLocalPrintJob(
  token: string,
  jobId: string,
  { timeoutMs = 20_000 }: { timeoutMs?: number } = {}
): Promise<LocalPrintJob> {
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    let job: LocalPrintJob
    try {
      job = await getLocalPrintJob(token, jobId)
    } catch (error) {
      throw new LocalPrintError(
        'Snapdragon accepted this job, but its current status could not be confirmed. Check the printer before printing another copy.',
        { code: 'job_status_unknown', jobId, outcomeUnknown: true, cause: error }
      )
    }
    if (job.status === 'submitted_to_os') return job
    if (job.status === 'failed') {
      throw new LocalPrintError(
        job.failureReason || job.message || 'Snapdragon could not print this job.',
        { code: 'job_failed', jobId, jobStatus: job.status }
      )
    }
    if (job.status === 'needs_attention') {
      const detail = job.failureReason || job.message
      throw new LocalPrintError(
        `Snapdragon may have sent this job to the printer. Check the printer before printing another copy.${detail ? ` ${detail}` : ''}`,
        {
          code: 'job_needs_attention',
          jobId,
          jobStatus: job.status,
          outcomeUnknown: true,
        }
      )
    }
    await new Promise((resolve) => window.setTimeout(resolve, 300))
  }
  throw new LocalPrintError(
    'Snapdragon is still working on this job. Check the printer before printing another copy. Choose Print again to check the same job.',
    { code: 'job_still_printing', jobId, outcomeUnknown: true }
  )
}

async function executeLocalPrintIntent({
  organizationId,
  token,
  intentKey,
  clientJobId,
  buildRequest,
}: LocalPrintIntentOptions): Promise<LocalPrintJob> {
  const retained = localPrintIntentCoordinator.loadIntent(organizationId, intentKey)
  const retainedClientJobId =
    retained?.clientJobId || clientJobId || `${intentKey}:${crypto.randomUUID()}`
  let jobId = retained?.jobId || ''

  if (!jobId && retained) {
    try {
      const job = await findLocalPrintJob(token, retainedClientJobId)
      jobId = job.id
      requireLocalPrintIntentRetention(organizationId, intentKey, {
        clientJobId: retainedClientJobId,
        jobId,
      })
    } catch (error) {
      throw attachLocalPrintIntentRecovery(
        new LocalPrintError(
          'Snapdragon could not find the earlier print job. Check the printer, then confirm that another print is safe.',
          { code: 'job_status_unknown', outcomeUnknown: true, cause: error }
        ),
        organizationId,
        intentKey
      )
    }
  }

  if (!jobId) {
    requireLocalPrintIntentRetention(organizationId, intentKey, {
      clientJobId: retainedClientJobId,
      jobId: '',
    })

    let request: LocalPrintJobRequest
    try {
      request = await buildRequest(retainedClientJobId)
    } catch (error) {
      clearLocalPrintIntent(organizationId, intentKey)
      const code = readProperty(error, 'code')
      if (
        typeof code === 'string' &&
        [
          'local_print_access_disabled',
          'job_not_submitted',
          'receipt_protocol_unsupported',
        ].includes(code)
      ) {
        throw error
      }
      throw new LocalPrintError(
        'OneStem could not prepare this print document. Browser printing is still available.',
        { code: 'job_not_submitted', cause: error }
      )
    }

    try {
      const job = await submitLocalPrintJob(token, request)
      jobId = job.id
      retainLocalPrintIntent(organizationId, intentKey, {
        clientJobId: retainedClientJobId,
        jobId,
      })
    } catch (error) {
      if (localPrintNeedsPrinterCheck(error)) {
        retainLocalPrintIntent(organizationId, intentKey, {
          clientJobId: retainedClientJobId,
          jobId: optionalId(error, 'jobId') ?? '',
        })
      } else {
        clearLocalPrintIntent(organizationId, intentKey)
      }
      throw localPrintNeedsPrinterCheck(error)
        ? attachLocalPrintIntentRecovery(error, organizationId, intentKey)
        : error
    }
  }

  try {
    const job = await waitForLocalPrintJob(token, jobId)
    return job
  } catch (error) {
    if (readProperty(error, 'code') === 'job_failed')
      clearLocalPrintIntent(organizationId, intentKey)
    throw localPrintNeedsPrinterCheck(error)
      ? attachLocalPrintIntentRecovery(error, organizationId, intentKey)
      : error
  }
}

// A run can resolve to another tab's stored result, so its job is read again here.
export async function runLocalPrintIntent(
  options: LocalPrintIntentOptions
): Promise<LocalPrintJob> {
  return readLocalPrintJob(
    await localPrintIntentCoordinator.run({
      organizationId: options.organizationId,
      intentKey: options.intentKey,
      execute: () => executeLocalPrintIntent(options),
    })
  )
}
